import * as React from 'react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Form } from '@amzn/awsui-community-rjsf';
import validator from 'src/components/datausageagreement/cfWorkerFormValidator';
import getDefaultFormState from '@rjsf/utils/lib/schema/getDefaultFormState';
import { OmniDUAContext } from 'src/components/datausageagreement/widgets/OmniDUAContext';
import { ReadOnlyArrayFieldTemplate } from 'src/components/datausageagreement/templates/ReadOnlyArrayFieldTemplate';
import {
  DEFAULT_FIELDS,
  DEFAULT_WIDGETS,
  filterSchemaByContexts,
  sanitizeFormData,
  TEXT_ONLY_FIELDS,
  TEXT_ONLY_WIDGETS,
  useRjsfSchema,
} from 'src/components/datausageagreement/utils';
import { CompactFieldTemplate } from 'src/components/datausageagreement/templates/CompactFieldTemplate';

export interface UsageAgreementFormProps {
  formData: object;
  setFormData: React.Dispatch<React.SetStateAction<object>>;
  errors: any[];
  setErrors: any;
  schema: string;
  readOnly: boolean;
  textOnly?: boolean;
  header?: ReactNode;
  omniContext?: OmniDUAContext;
  contexts?: string[];
}

export const UsageAgreementForm = (props: UsageAgreementFormProps) => {
  const [propertyOrder, setPropertyOrder] = useState<string[]>([]);
  const [uiSchemaFromJson, setUiSchemaFromJson] = useState<any>(null);

  const rjsfSchema = useRjsfSchema(props.schema, setPropertyOrder, setUiSchemaFromJson);
  const filteredSchema = useMemo(
    () => filterSchemaByContexts(rjsfSchema, props.contexts || ['default']),
    [rjsfSchema, props.contexts],
  );
  const sanitizedFormData = useMemo(() => sanitizeFormData(props.formData), [props.formData]);
  // Don't filter the order array - let RJSF handle conditional field visibility
  // Conditional fields from allOf need to be in ui:order to appear in the correct position
  const filteredOrder = useMemo(() => {
    return propertyOrder;
  }, [propertyOrder]);

  useEffect(() => {
    if (!props.formData || Object.keys(props.formData).length === 0) {
      const defaults = getDefaultFormState(validator, filteredSchema, props.formData, filteredSchema, true) as object;
      // prevent infinite loops if the form values are empty and have no defaults
      if (defaults && Object.keys(defaults).length > 0) {
        props.setFormData(defaults);
      }
    }
  }, [props.formData, filteredSchema]);

  const widgets = useMemo(() => (props.textOnly ? TEXT_ONLY_WIDGETS : DEFAULT_WIDGETS), [props.textOnly]);
  const fields = useMemo(() => (props.textOnly ? TEXT_ONLY_FIELDS : DEFAULT_FIELDS), [props.textOnly]);
  const templates = useMemo(
    () =>
      props.textOnly
        ? { ArrayFieldTemplate: ReadOnlyArrayFieldTemplate, FieldTemplate: CompactFieldTemplate }
        : { FieldTemplate: CompactFieldTemplate },
    [props.textOnly],
  );

  const getRootLevelErrorText = () => {
    const rootLevelErrors = props.errors.filter((error) => error.schemaPath == '#');
    if (rootLevelErrors.length == 0) {
      return;
    }
    return rootLevelErrors[0].message;
  };

  let uiSchema = {
    'ui:widget': 'checkbox',
    'ui:submitButtonOptions': {
      norender: true,
    },
    // Merge in uiSchema from JSON if provided
    ...(uiSchemaFromJson || {}),
  };
  if (filteredOrder?.length > 0) {
    uiSchema['ui:order'] = filteredOrder;
    if (uiSchema['ui:order']?.at(-1) !== '*') {
      uiSchema['ui:order'].push('*');
    }
  }
  // Fields with recursive $ref schemas get stripped by omitExtraData/liveOmit.
  // Preserve their full data by merging back what the field component reported.
  const treeDataRef = React.useRef<Record<string, any>>({});

  const fieldFormContext = useMemo(
    () => ({
      omniContext: props.omniContext,
      onTreeFieldChange: (key: string, value: any) => {
        treeDataRef.current[key] = value;
      },
    }),
    [props.omniContext],
  );

  return (
    <Form
      formContext={fieldFormContext}
      header={props.header}
      readonly={props.readOnly}
      validator={validator}
      liveValidate={true}
      schema={filteredSchema}
      formData={sanitizedFormData}
      onChange={(data) => {
        const sanitizedData = sanitizeFormData(data.formData);
        // Restore tree fields that omitExtraData stripped
        for (const [key, value] of Object.entries(treeDataRef.current)) {
          sanitizedData[key] = value;
        }
        props.setFormData(sanitizedData);
        props.setErrors(data.errors);
      }}
      transformErrors={() => {
        return props.errors;
      }}
      extraErrorsBlockSubmit={true}
      showErrorList={'bottom'}
      errorText={getRootLevelErrorText()}
      uiSchema={uiSchema}
      noHtml5Validate={true}
      widgets={widgets}
      fields={fields}
      omitExtraData={true}
      liveOmit={true}
      templates={templates}
    />
  );
};
